/**
 * EventWeatherRow - Shows weather forecast for the event's date.
 * Only displays for future events within the forecast range (~7 days).
 */

import { ActivityIndicator, StyleSheet, Text, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { Colors } from '@/constants/theme';
import { Spacing } from '@/constants/spacing';
import { getBodyFont } from '@/constants/typography';
import { useWeatherForDate } from '@/hooks/use-weather';
import { getWeatherIconColor } from '@/lib/weather/WeatherIconColors';

type EventWeatherRowProps = {
  date: string; // YYYY-MM-DD
  palette: typeof Colors.light;
};

export function EventWeatherRow({ date, palette }: EventWeatherRowProps) {
  const { forecast, isLoading } = useWeatherForDate(date);

  // Don't render if no forecast available (past date or beyond forecast range)
  if (!forecast && !isLoading) return null;

  return (
    <View style={styles.container}>
      <View style={styles.iconContainer}>
        <Ionicons
          name={(forecast?.icon ?? 'cloud-outline') as any}
          size={18}
          color={forecast ? getWeatherIconColor(forecast.icon) : palette.textMuted}
        />
      </View>

      <View style={styles.content}>
        {isLoading ? (
          <ActivityIndicator size="small" color={palette.textMuted} />
        ) : forecast ? (
          <View style={styles.forecastRow}>
            <Text style={[styles.tempText, { color: palette.text, fontFamily: getBodyFont() }]}>
              {forecast.high}° / {forecast.low}°
            </Text>
            <Text
              style={[styles.descriptionText, { color: palette.textMuted, fontFamily: getBodyFont() }]}
              numberOfLines={1}
            >
              {forecast.description}
            </Text>
            {forecast.precipChance > 20 && (
              <View style={styles.precipBadge}>
                <Ionicons name="water-outline" size={12} color={palette.primary} />
                <Text style={[styles.precipText, { color: palette.primary, fontFamily: getBodyFont() }]}>
                  {forecast.precipChance}%
                </Text>
              </View>
            )}
          </View>
        ) : null}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    paddingHorizontal: Spacing.lg,
    paddingVertical: Spacing.sm,
  },
  iconContainer: {
    width: 40,
    alignItems: 'center',
    paddingTop: 2,
  },
  content: {
    flex: 1,
    justifyContent: 'center',
  },
  forecastRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.sm,
  },
  tempText: {
    fontSize: 14,
    fontWeight: '600',
  },
  descriptionText: {
    fontSize: 13,
    flex: 1,
  },
  precipBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
  },
  precipText: {
    fontSize: 12,
    fontWeight: '600',
  },
});
